import { BaseApiClient } from './base'

export class AdminApi extends BaseApiClient {
  async getAdminStats(): Promise<{ stats: any }> {
    return this.request('/admin/stats')
  }

  async getAdminBooks(
    includeHidden: boolean = false, 
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      genres?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<{ books: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('include_hidden', includeHidden.toString())
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    
    if (options?.languages && options.languages.length > 0) {
      params.append('languages', options.languages.join(','))
    }
    if (options?.genres && options.genres.length > 0) {
      params.append('genres', options.genres.join(','))
    }
    if (options?.statuses && options.statuses.length > 0) {
      params.append('statuses', options.statuses.join(','))
    }
    if (options?.search) {
      params.append('search', options.search)
    }
    if (options?.sortBy) {
      params.append('sort_by', options.sortBy)
      params.append('sort_order', options.sortOrder || 'desc')
    }
    
    return this.request(`/admin/books?${params.toString()}`)
  }

  async getAdminGenres(): Promise<{ genres: string[] }> {
    return this.request('/admin/genres')
  }

  async getAdminBook(bookId: string): Promise<{ book: any }> {
    return this.request(`/admin/books/${bookId}`)
  }

  async getAdminTranslations(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      levels?: string[]
      formats?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<{ translations: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    
    if (options?.languages && options.languages.length > 0) {
      params.append('languages', options.languages.join(','))
    }
    if (options?.levels && options.levels.length > 0) {
      params.append('levels', options.levels.join(','))
    }
    if (options?.formats && options.formats.length > 0) {
      params.append('formats', options.formats.join(','))
    }
    if (options?.statuses && options.statuses.length > 0) {
      params.append('statuses', options.statuses.join(','))
    }
    if (options?.search) {
      params.append('search', options.search)
    }
    if (options?.sortBy) {
      params.append('sort_by', options.sortBy)
      params.append('sort_order', options.sortOrder || 'desc')
    }
    
    return this.request(`/admin/translations?${params.toString()}`)
  }

  async getAdminUserTranslations(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      levels?: string[]
      formats?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<{ translations: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    
    if (options?.languages && options.languages.length > 0) {
      params.append('languages', options.languages.join(','))
    }
    if (options?.levels && options.levels.length > 0) {
      params.append('levels', options.levels.join(','))
    }
    if (options?.formats && options.formats.length > 0) {
      params.append('formats', options.formats.join(','))
    }
    if (options?.statuses && options.statuses.length > 0) {
      params.append('statuses', options.statuses.join(','))
    }
    if (options?.search) {
      params.append('search', options.search)
    }
    if (options?.sortBy) {
      params.append('sort_by', options.sortBy)
      params.append('sort_order', options.sortOrder || 'desc')
    }
    
    return this.request(`/admin/user-translations?${params.toString()}`)
  }

  async getAdminJobs(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      statuses?: string[]
      languages?: string[]
      levels?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<{ jobs: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    
    if (options?.statuses && options.statuses.length > 0) {
      params.append('statuses', options.statuses.join(','))
    }
    if (options?.languages && options.languages.length > 0) {
      params.append('languages', options.languages.join(','))
    }
    if (options?.levels && options.levels.length > 0) {
      params.append('levels', options.levels.join(','))
    }
    if (options?.search) {
      params.append('search', options.search)
    }
    if (options?.sortBy) {
      params.append('sort_by', options.sortBy)
      params.append('sort_order', options.sortOrder || 'desc')
    }
    
    return this.request(`/admin/jobs?${params.toString()}`)
  }

  async createOriginalBook(data: {
    title_original: string
    title_international: string
    author_original: string
    author_international: string
    description?: string
    genre?: string
    original_language?: string
    format?: string
    estimated_pages?: number
    original_file: File
    cover_image?: File
  }): Promise<{ book_id: string }> {
    const formData = new FormData()
    formData.append('title_original', data.title_original)
    formData.append('title_international', data.title_international)
    formData.append('author_original', data.author_original)
    formData.append('author_international', data.author_international)
    if (data.description) formData.append('description', data.description)
    if (data.genre) formData.append('genre', data.genre)
    if (data.original_language) formData.append('original_language', data.original_language)
    if (data.format) formData.append('format', data.format)
    if (data.estimated_pages) formData.append('estimated_pages', data.estimated_pages.toString())
    formData.append('original_file', data.original_file)
    if (data.cover_image) formData.append('cover_image', data.cover_image)

    return this.requestFormData('/admin/books', formData, { method: 'POST' })
  }

  async updateOriginalBook(bookId: string, data: {
    title_original?: string
    title_international?: string
    author_original?: string
    author_international?: string
    original_language?: string
    description?: string
    genre?: string
    cover_image?: File
  }): Promise<void> {
    const formData = new FormData()
    if (data.title_original) formData.append('title_original', data.title_original)
    if (data.title_international) formData.append('title_international', data.title_international)
    if (data.author_original) formData.append('author_original', data.author_original)
    if (data.author_international) formData.append('author_international', data.author_international)
    if (data.original_language) formData.append('original_language', data.original_language)
    if (data.description) formData.append('description', data.description)
    if (data.genre) formData.append('genre', data.genre)
    if (data.cover_image) formData.append('cover_image', data.cover_image)

    return this.requestFormData(`/admin/books/${bookId}`, formData, { method: 'PUT' })
  }

  async deleteOriginalBook(bookId: string): Promise<void> {
    return this.request(`/admin/books/${bookId}`, {
      method: 'DELETE',
    })
  }

  async toggleBookHidden(bookId: string, hidden: boolean): Promise<void> {
    return this.request(`/admin/books/${bookId}/hide`, {
      method: 'POST',
      body: JSON.stringify({ hidden }),
    })
  }

  async recalculateBookPages(bookId: string): Promise<{ success: boolean; estimated_pages: number }> {
    return this.request(`/admin/books/${bookId}/recalculate-pages`, {
      method: 'POST',
    })
  }

  async bulkRecalculateBookPages(bookIds: string[]): Promise<{
    success: boolean
    results: {
      success: Array<{ book_id: string; estimated_pages: number }>
      failed: Array<{ book_id: string; error: string }>
    }
    updated_count: number
    failed_count: number
  }> {
    return this.request('/admin/books/bulk/recalculate-pages', {
      method: 'POST',
      body: JSON.stringify({ book_ids: bookIds }),
    })
  }

  async deleteTranslation(translationId: string): Promise<void> {
    return this.request(`/admin/translations/${translationId}`, {
      method: 'DELETE',
    })
  }

  async cancelJob(jobId: number): Promise<{ success: boolean; translation_deleted: boolean }> {
    return this.request(`/admin/jobs/${jobId}`, {
      method: 'DELETE',
    })
  }

  async republishJob(jobId: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/jobs/${jobId}/republish`, {
      method: 'POST',
    })
  }

  async republishOrphanedJobs(): Promise<{ success: boolean; republished_count: number }> {
    return this.request('/admin/jobs/republish-orphaned', {
      method: 'POST',
    })
  }

  async updateTranslationStatus(translationId: string, status: string): Promise<void> {
    return this.request(`/admin/translations/${translationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  }

  async analyzeBookFile(file: File): Promise<{
    success: boolean
    metadata: {
      title_original: string | null
      title_international: string | null
      author_original: string | null
      author_international: string | null
      description: string | null
      cover_image_base64: string | null
      detected_language: string | null
      format: string | null
      estimated_pages: number | null
    }
  }> {
    const formData = new FormData()
    formData.append('file', file)

    return this.requestFormData('/admin/books/analyze-file', formData, { method: 'POST' })
  }

  async addTranslation(data: {
    book_id: string
    language: string
    cefr_level: string | null
    file: File
  }): Promise<{ success: boolean, translation_id: string }> {
    const formData = new FormData()
    formData.append('language', data.language)
    if (data.cefr_level) formData.append('cefr_level', data.cefr_level)
    formData.append('file', data.file)

    return this.requestFormData(`/admin/books/${data.book_id}/translations`, formData, { method: 'POST' })
  }

  async queueAutoTranslation(bookId: string, language: string, cefrLevel: string): Promise<{ success: boolean, job_id: string, translation_id: string, llm: string }> {
    return this.request(`/admin/books/${bookId}/queue-translation`, {
      method: 'POST',
      body: JSON.stringify({ language, cefr_level: cefrLevel }),
    })
  }

  async getAdminSettings(): Promise<{ success: boolean, settings: Record<string, string> }> {
    return this.request('/admin/settings')
  }

  async updateAdminSetting(key: string, value: string): Promise<{ success: boolean }> {
    return this.request(`/admin/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    })
  }

  async getTranslationJobStatus(translationId: string): Promise<{ 
    success: boolean
    job: {
      id: number
      status: string
      attempts: number
      max_attempts: number
      created_at: string
      updated_at: string
      last_error: string | null
      target_language: string
      level: string
    }
  }> {
    return this.request(`/translations/${translationId}/job-status`)
  }

  async getTranslationErrorLogs(translationId: string): Promise<{
    success: boolean
    logs: Array<{
      id: number
      book_id: string
      book_title: string | null
      translation_id: number
      job_id: number
      batch_number: number | null
      target_language: string
      level: string
      raw_text: string
      raw_response: string | null
      error_message: string
      timestamp: string
    }>
  }> {
    return this.request(`/translations/${translationId}/error-logs`)
  }

  async restartTranslationJob(translationId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/translations/${translationId}/restart-job`, {
      method: 'POST',
    })
  }

  async analyzeBookForSplit(bookId: string): Promise<{
    success: boolean
    sections: Array<{
      title: string
      href: string
      children: any[]
      recommended: boolean
    }>
    original_metadata: {
      title: string | null
      author: string | null
      description: string | null
      language: string | null
    }
    cover_url: string | null
  }> {
    return this.request(`/admin/books/${bookId}/analyze-for-split`, {
      method: 'POST',
    })
  }

  async splitBook(
    bookId: string, 
    sections: any[], 
    coverReplacements: Record<string, string> = {},
    options?: {
      generateCover?: boolean
      generateDescription?: boolean
      promptTemplateCover?: string
      promptTemplateDescription?: string
    }
  ): Promise<{
    success: boolean
    created_books: Array<{
      book_id: string
      title: string
    }>
  }> {
    return this.request(`/admin/books/${bookId}/split`, {
      method: 'POST',
      body: JSON.stringify({
        sections,
        cover_replacements: coverReplacements,
        generate_cover: options?.generateCover ?? false,
        generate_description: options?.generateDescription ?? false,
        prompt_template_cover: options?.promptTemplateCover,
        prompt_template_description: options?.promptTemplateDescription
      }),
    })
  }

  async getAllJobs(): Promise<{
    success: boolean
    jobs: Array<{
      id: number
      status: string
      book_id: string | null
      book_title: string | null
      target_language: string | null
      level: string | null
      attempts: number
      max_attempts: number
      last_error: string | null
      created_at: string
      updated_at: string
    }>
  }> {
    return this.request('/admin/jobs')
  }

  async bulkDeleteBooks(bookIds: string[]): Promise<{ success: boolean, deleted_count: number }> {
    return this.request('/admin/books/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ book_ids: bookIds }),
    })
  }

  async bulkQueueTranslations(data: {
    book_ids: string[]
    translations: Array<{ language: string; cefr_level: string }>
    skip_duplicates?: boolean
  }): Promise<{ success: boolean, queued_count: number, skipped_count: number }> {
    return this.request('/admin/books/bulk-queue-translations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async generateBookCover(
    bookId: string, 
    promptTemplate?: string,
    options?: { model?: string; useDescription?: boolean }
  ): Promise<{ success: boolean, cover_base64: string }> {
    const requestBody: { prompt_template?: string; model?: string; use_description?: boolean } = {}
    if (promptTemplate) {
      requestBody.prompt_template = promptTemplate
    }
    if (options?.model) {
      requestBody.model = options.model
    }
    if (options?.useDescription) {
      requestBody.use_description = options.useDescription
    }
    return this.request(`/admin/books/${bookId}/generate-cover`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
  }

  async bulkGenerateCovers(
    bookIds: string[], 
    promptTemplate?: string,
    options?: { model?: string; useDescription?: boolean }
  ): Promise<{
    success: boolean
    results: Array<{
      book_id: string
      title: string
      success: boolean
      cover_base64?: string
      cover_paths?: { full: string; medium: string; small: string }
      book_file_updated?: boolean
      error?: string
    }>
    total: number
    successful: number
  }> {
    const requestBody: { book_ids: string[]; prompt_template?: string; model?: string; use_description?: boolean } = { book_ids: bookIds }
    if (promptTemplate) {
      requestBody.prompt_template = promptTemplate
    }
    if (options?.model) {
      requestBody.model = options.model
    }
    if (options?.useDescription) {
      requestBody.use_description = options.useDescription
    }
    return this.request('/admin/books/bulk-generate-covers', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
  }

  async generateBookDescription(
    title: string,
    author: string,
    promptTemplate: string
  ): Promise<{ success: boolean; description: string }> {
    return this.request('/admin/books/generate-description', {
      method: 'POST',
      body: JSON.stringify({
        title,
        author,
        prompt_template: promptTemplate,
      }),
    })
  }

  async bulkGenerateDescriptions(
    bookIds: string[],
    promptTemplate: string
  ): Promise<{
    success: boolean
    results: Array<{
      book_id: string
      title: string
      success: boolean
      description?: string
      error?: string
    }>
    total: number
    successful: number
  }> {
    return this.request('/admin/books/bulk-generate-descriptions', {
      method: 'POST',
      body: JSON.stringify({
        book_ids: bookIds,
        prompt_template: promptTemplate,
      }),
    })
  }

  async generateDescriptionCover(
    bookId: string,
    descriptionPromptTemplate: string,
    coverPromptTemplate: string,
    options?: { coverModel?: string }
  ): Promise<{
    success: boolean
    description: string
    cover_base64: string
    cover_paths: { full: string; medium: string; small: string }
    book_file_updated: boolean
  }> {
    const requestBody: {
      description_prompt_template: string
      cover_prompt_template: string
      cover_model?: string
    } = {
      description_prompt_template: descriptionPromptTemplate,
      cover_prompt_template: coverPromptTemplate,
    }
    if (options?.coverModel) {
      requestBody.cover_model = options.coverModel
    }
    return this.request(`/admin/books/${bookId}/generate-description-cover`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
  }

  async bulkGenerateDescriptionsCovers(
    bookIds: string[],
    descriptionPromptTemplate: string,
    coverPromptTemplate: string,
    options?: { coverModel?: string }
  ): Promise<{
    success: boolean
    results: Array<{
      book_id: string
      title: string
      success: boolean
      description?: string
      cover_paths?: { full: string; medium: string; small: string }
      book_file_updated?: boolean
      error?: string
    }>
    total: number
    successful: number
  }> {
    const requestBody: {
      book_ids: string[]
      description_prompt_template: string
      cover_prompt_template: string
      cover_model?: string
    } = {
      book_ids: bookIds,
      description_prompt_template: descriptionPromptTemplate,
      cover_prompt_template: coverPromptTemplate,
    }
    if (options?.coverModel) {
      requestBody.cover_model = options.coverModel
    }
    return this.request('/admin/books/bulk-generate-descriptions-covers', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })
  }

  async getAdminTransactions(page: number = 1, pageSize: number = 10): Promise<{ transactions: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    return this.request(`/admin/transactions?${params.toString()}`)
  }

  async getAdminMemberships(page: number = 1, pageSize: number = 100): Promise<{ memberships: any[]; pagination?: any }> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    return this.request(`/admin/memberships?${params.toString()}`)
  }


  async fixUserBalance(userId: string): Promise<{
    success: boolean
    old_balance?: number
    new_balance?: number
    total_purchases?: number
    total_deductions?: number
    message: string
  }> {
    return this.request(`/admin/users/${userId}/fix-balance`, {
      method: 'POST',
    })
  }

  async bulkUploadBooks(files: File[]): Promise<{
    success: boolean
    uploaded_count: number
    failed_count: number
    results: Array<{
      book_id: string
      filename: string
      title: string
      author: string
      estimated_pages: number | null
    }>
    failed: Array<{
      filename: string
      error: string
    }>
  }> {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })
    
    return this.requestFormData('/admin/books/bulk-upload', formData, { method: 'POST' })
  }

  async syncStripeTransactions(userId: string): Promise<{
    success: boolean
    message: string
    synced_purchases: Array<{
      purchase_id: string
      payment_intent_id: string
      amount_usd: number
      created: number
      status: string
      description: string | null
    }>
    extra_db_transactions: Array<{
      type: string
      id: string
      payment_intent_id: string
      amount_usd: number
      created_at: string | null
      description: string | null
    }>
    subscription_status: {
      id: string
      status: string
      current_period_end: number
      cancel_at_period_end: boolean
      db_status: string
      matches_db: boolean
      error?: string
    } | null
    balance: {
      old_balance: number
      new_balance: number
      total_purchases: number
      total_deductions: number
      updated: boolean
    }
  }> {
    return this.request(`/admin/users/${userId}/sync-stripe`, {
      method: 'POST',
    })
  }
}

